<template>
  <!-- Difficulty Index -->
  <v-card outlined class="mb-6 pa-6">
    <!-- Header -->
    <v-row align="center" justify="space-between" class="mb-6">
      <v-col cols="12" md="6">
        <h2 class="text-h6 font-weight-bold">{{ $t("examAnalytics.difficulty") }}</h2>
      </v-col>
      <v-col cols="12" md="6" class="d-flex justify-end align-center">
        <v-select
          v-model="difficultyFilter"
          :items="[
            { text: $t('examAnalytics.All Questions'), value: 'all' },
            { text: $t('examAnalytics.Too Easy'), value: 'easy' },
            { text: $t('examAnalytics.Moderate'), value: 'moderate' },
            { text: $t('examAnalytics.Too Hard'), value: 'hard' }
          ]"
          dense
          outlined
          hide-details
          style="max-width: 200px"
        />
      </v-col>
    </v-row>

    <!-- Legend -->
    <v-row class="mb-4">
      <v-col cols="auto" class="d-flex align-center">
        <v-icon small color="blue" class="mr-1">mdi-circle</v-icon>
        <span class="caption text--secondary">{{ $t("examAnalytics.Too Easy") }} (&gt; 0.9)</span>
      </v-col>
      <v-col cols="auto" class="d-flex align-center">
        <v-icon small color="green" class="mr-1">mdi-circle</v-icon>
        <span class="caption text--secondary">{{ $t("examAnalytics.Moderate") }} (0.3 - 0.9)</span>
      </v-col>
      <v-col cols="auto" class="d-flex align-center">
        <v-icon small color="red" class="mr-1">mdi-circle</v-icon>
        <span class="caption text--secondary">{{ $t("examAnalytics.Too Hard") }} (&lt; 0.3)</span>
      </v-col>
    </v-row>

    <!-- Table and Chart Grid -->
    <v-row dense>
      <v-col cols="12" md="6">
        <v-card outlined>
          <v-simple-table dense>
            <thead>
              <tr>
                <th>{{ $t("examAnalytics.Question") }} #</th>
                <th class="text-center">{{ $t("examAnalytics.Correct Answers") }}</th>
                <th class="text-center">{{ $t("examAnalytics.Total Students") }}</th>
                <th class="text-center">{{ $t("examAnalytics.p-value") }}</th>
                <th>{{ $t("examAnalytics.Difficulty Level") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in paginatedQuestions"
                :key="`diff-${item.question}`"
                :class="getPValueRowClass(item.pValue)"
              >
                <td :title="item.questionTitle" class="text-body-2 font-weight-medium">
                  {{ item.question }}
                </td>
                <td class="text-center">{{ item.correctAnswers }}</td>
                <td class="text-center">{{ item.totalStudents }}</td>
                <td
                  class="text-center font-weight-bold"
                  :class="{
                    'blue--text text--darken-2': item.pValue > 0.9,
                    'green--text text--darken-2': item.pValue >= 0.3 && item.pValue <= 0.9,
                    'red--text text--darken-2': item.pValue < 0.3
                  }"
                >
                  {{ formatSmartDecimal(item.pValue) }}
                </td>
                <td class="font-weight-bold">
                  <v-chip
                    :color="
                      item.pValue > 0.9
                        ? 'blue lighten-4'
                        : item.pValue >= 0.3
                        ? 'green lighten-4'
                        : 'red lighten-4'
                    "
                    :text-color="
                      item.pValue > 0.9
                        ? 'blue darken-2'
                        : item.pValue >= 0.3
                        ? 'green darken-2'
                        : 'red darken-2'
                    "
                    small
                    label
                  >
                    {{ $t("examAnalytics." + item.difficultyLevel) }}
                  </v-chip>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
          <v-divider></v-divider>
          <v-row align="center" justify="space-between" class="ma-1">
            <!-- Student Count -->
            <v-col cols="12" md="3" class="text-start">
              <span class="grey--text font-weight-medium"
                >{{ filteredDifficultyData().length }} {{ $t("examAnalytics.Question") }}</span
              >
            </v-col>

            <!-- Pagination Control -->
            <v-col cols="12" md="6" class="text-center">
              <v-pagination v-model="page" :length="pageCount" :total-visible="15" color="primary" />
            </v-col>

            <!-- Items Per Page -->
            <v-col cols="12" md="3" class="text-end">
              <v-select
                v-model="itemsPerPage"
                :items="[5, 10, 15, 25]"
                :label="$t('rowsPerPage')"
                dense
                outlined
                hide-details 
              />
            </v-col>
          </v-row>

        </v-card>
      </v-col>

      <!-- Chart -->
      <v-col cols="12" md="6">
        <v-card outlined>

            <Bar
          :key="difficultyChartData.labels.length"
          :datasets="difficultyChartData.datasets"
          :labels="difficultyChartData.labels"
          :chart-options="difficultyChartOptions"
          class="mb-3"
        ></Bar>


        </v-card>

      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import Bar from "../../charts/Bar.vue";

export default {
  name: "Difficulty",
  components: { Bar },
  props: {
    ExamStatisticsData: {
      type: Object,
      required: true
    },
    Questions: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      page: 1,
      itemsPerPage: 10,
      difficultyData: [],
      difficultyFilter: "all",

      difficultyChartData: {
        labels: [],
        datasets: []
      },
      difficultyChartOptions: {
        indexAxis: "y", //
        responsive: true,
        animation: true,
        plugins: {
          legend: { display: false },
          datalabels: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: ctx =>
                `${ctx.label}: ${ctx.parsed.x.toFixed(2)} (${this.$t("examAnalytics.p-value")})`
            }
          },
          datalabels: {
            color: this.$vuetify.theme.dark ? "white" : "#000",
            anchor: "end",
            align: "end",
            anchor: "end",
            textAlign: "center",
            formatter: value => value.toFixed(2), // Show only 2 decimals
            font: {
              size: 12
            }
          }
        },
        scales: {
          x: {
            title: {
              display: true,
              text: this.$t("examAnalytics.p-value")
            },
            ticks: {
              color: "#666"
            },
            grid: {
              color: "#ddd",
              borderDash: [4, 4]
            }
          },
          y: {
            ticks: {
              color: "#666"
            }
          }
        }
      }
    };
  },
  created() {
    this.difficultyData = this.ExamStatisticsData.questions.map((item, index) => ({
      question: `${this.$t("examAnalytics.Q")} ${index + 1}`,
      questionTitle: this.Questions.filter(m => m.questionGuid == item.questionGuid)[0]
        .questionTitle,
      correctAnswers: item.correctAnswers,
      totalStudents: item.totalStudents,
      pValue: item.pValue,
      difficultyLevel: item.difficultyLevel
    }));
  },
  computed: {

       pageCount() {
      return Math.ceil(this.filteredDifficultyData().length / this.itemsPerPage);
    },
    paginatedQuestions() {
      const start = (this.page - 1) * this.itemsPerPage;
      return this.filteredDifficultyData().slice(start, start + this.itemsPerPage);
    },
    isDark() {
      return this.$vuetify.theme.dark;
    },

  },
  mounted() {
    this.$nextTick(() => {
      this.computeDifficultyChart();
    });
  },
  methods: {
    filteredDifficultyData() {
      if (this.difficultyFilter === "all") {
        return this.difficultyData;
      } else if (this.difficultyFilter === "easy") {
        return this.difficultyData.filter(item => item.pValue > 0.9);
      } else if (this.difficultyFilter === "moderate") {
        return this.difficultyData.filter(item => item.pValue >= 0.3 && item.pValue <= 0.9);
      } else if (this.difficultyFilter === "hard") {
        return this.difficultyData.filter(item => item.pValue < 0.3);
      }
      return this.difficultyData;
    },
     getPValueRowClass(pValue) {
      if (pValue > 0.9) return this.isDark ? "" : "blue lighten-5";
      if (pValue >= 0.3) return this.isDark ? "" : "green lighten-5";
      return this.isDark ? "" : "red lighten-5";
    },
    computeDifficultyChart() {
      const questions = this.difficultyData.map(item => item.question);
      const pValues = this.difficultyData.map(item => item.pValue);
      const backgroundColors = this.difficultyData.map(item => {
        if (item.pValue > 0.9) return "#3B82F6"; // Too Easy
        if (item.pValue >= 0.3) return "#10B981"; // Moderate
        return "#EF4444"; // Too Hard
      });

      this.difficultyChartData = {
        labels: questions,
        datasets: [
          {
            label: "p-value",
            data: pValues,
            backgroundColor: backgroundColors,
            barPercentage: 1,
            categoryPercentage: 0.7,
            borderRadius: 4
          }
        ]
      };
    }
  }
};
</script>
